import React, { useState, useRef } from 'react';
import QuickMenu from '../../components/QuickMenu/QuickMenu';
import neighborhoods from './neighborhoods.json';

const API_URL = process.env.REACT_APP_API_URL;

const ReportIncident = () => {
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const MAX_IMAGES = 5;
  const MAX_MESSAGE_LENGTH = 1000;

  const validateMessage = (value) => {
    return value.slice(0, MAX_MESSAGE_LENGTH);
  };

  const validateImageFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Sadece resim dosyaları (JPEG, PNG, GIF, WebP) kabul edilir.' };
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return { isValid: false, error: 'Dosya boyutu 5MB\'dan küçük olmalıdır.' };
    }
    
    return { isValid: true };
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    processImageFiles(droppedFiles);
  };

  const handleImageSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processImageFiles(selectedFiles);
    e.target.value = '';
  };

  const processImageFiles = (files) => {
    if (images.length >= MAX_IMAGES) {
      alert(`En fazla ${MAX_IMAGES} resim ekleyebilirsiniz.`);
      return;
    }

    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      const validation = validateImageFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        invalidFiles.push({ name: file.name, error: validation.error });
      }
    });

    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles.map(f => `${f.name}: ${f.error}`).join('\n');
      alert(`Bazı dosyalar eklenemedi:\n${errorMessages}`);
    }

    const remainingSlots = MAX_IMAGES - images.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);
    
    if (filesToAdd.length > 0) {
      setImages(prev => [...prev, ...filesToAdd]);
    }

    if (validFiles.length > remainingSlots) {
      alert(`En fazla ${MAX_IMAGES} resim ekleyebilirsiniz. ${validFiles.length - remainingSlots} dosya eklenmedi.`);
    }

    if (errors.images) {
      setErrors(prev => ({ ...prev, images: '' }));
    }
  };

  const removeImage = (indexToRemove) => {
    setImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedNeighborhood) {
      newErrors.neighborhood = 'Mahalle seçimi zorunludur.';
    }

    if (!message.trim()) {
      newErrors.message = 'Olay detayı zorunludur.';
    } else if (message.trim().length < 10) {
      newErrors.message = 'Olay detayı en az 10 karakter olmalıdır.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) {
      return;
    }

    const fd = new FormData();
    fd.append('Neighborhood', selectedNeighborhood);
    fd.append('Message', message.trim());

    images.forEach((img) => {
      fd.append('images', img);
    });

    try {
      setIsSubmitting(true);
      const res = await fetch(`${API_URL}/ReportIncident`, { 
        method: 'POST', 
        body: fd 
      });
      
      const data = await res.json();
      if (!res.ok || !data?.isSuccess) throw new Error(data?.resultMessage || 'Hata');
      
      alert('Bildiriminiz alındı. Teşekkürler.');
      
      setSelectedNeighborhood('');
      setMessage('');
      setImages([]);
      setErrors({});
      
    } catch (err) {
      alert('Gönderim sırasında hata oluştu: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageChange = (e) => {
    const validatedValue = validateMessage(e.target.value);
    setMessage(validatedValue);
    
    if (errors.message) {
      setErrors(prev => ({ ...prev, message: '' }));
    }
  };

  const handleNeighborhoodChange = (e) => {
    setSelectedNeighborhood(e.target.value);
    
    if (errors.neighborhood) {
      setErrors(prev => ({ ...prev, neighborhood: '' }));
    }
  };

  return (
    <div className="page-container">
      <div className="page-content">
        <QuickMenu />
      
        <main className="main-content">
          <h2 className="page-title">Bize Bildir</h2>
          <div className="content-wrapper">
            <form onSubmit={handleSubmit} className="report-form">
              <div className="form-group">
                <label htmlFor="neighborhood">Mahalle Seçin *</label>
                <select
                  id="neighborhood"
                  value={selectedNeighborhood}
                  onChange={handleNeighborhoodChange}
                  required
                  className={errors.neighborhood ? 'error' : ''}
                >
                  <option value="">Mahalle seçiniz...</option>
                  {neighborhoods.neighborhoods.map((neighborhood) => (
                    <option key={neighborhood} value={neighborhood}>
                      {neighborhood}
                    </option>
                  ))}
                </select>
                {errors.neighborhood && (
                  <small className="error-text">{errors.neighborhood}</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  Olay Detayı * 
                  <small>({message.length}/{MAX_MESSAGE_LENGTH} karakter)</small>
                </label>
                <textarea
                  id="message"
                  rows={4}
                  value={message}
                  onChange={handleMessageChange}
                  placeholder="Lütfen olayı detaylı bir şekilde açıklayınız..."
                  required
                  maxLength={MAX_MESSAGE_LENGTH}
                  className={errors.message ? 'error' : ''}
                />
                {errors.message && (
                  <small className="error-text">{errors.message}</small>
                )}
              </div>

              <div className="form-group">
                <label>
                  Fotoğraf Ekle 
                  <small>(En fazla {MAX_IMAGES} resim, her biri max 5MB)</small>
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  multiple
                  style={{ display: 'none' }}
                />
                
                <div
                  className={`image-drop-zone ${images.length >= MAX_IMAGES ? 'disabled' : ''}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleImageDrop}
                  onClick={() => {
                    if (images.length < MAX_IMAGES) {
                      fileInputRef.current.click();
                    }
                  }}
                >
                  {images.length < MAX_IMAGES ? (
                    <p>Fotoğrafları buraya sürükleyip bırakın veya tıklayarak seçin</p>
                  ) : (
                    <p>Maksimum resim sayısına ulaşıldı ({MAX_IMAGES})</p>
                  )}
                  
                  {images.length > 0 && (
                    <div className="image-previews">
                      {images.map((image, index) => (
                        <div key={index} className="image-preview-container">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`Preview ${index + 1}`}
                            className="image-preview"
                          />
                          <button
                            type="button"
                            className="remove-image-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(index);
                            }}
                            title="Resmi kaldır"
                          >
                            ×
                          </button>
                          <small className="image-info">
                            {image.name} ({(image.size / 1024 / 1024).toFixed(1)} MB)
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <small className="upload-info">
                  Desteklenen formatlar: JPEG, PNG, GIF, WebP
                </small>
              </div>

              <button 
                type="submit" 
                className={`submit-button ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting || (images.length === 0 && !message.trim())}
                aria-busy={isSubmitting}
                aria-live="polite"
              >
                {isSubmitting ? <><span className="spinner" /> Gönderiliyor…</> : 'Gönder'}
              </button>
            </form>
          </div>
        </main>
      </div>

      <style jsx>{`
        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .form-group label small {
          font-weight: normal;
          color: #6c757d;
          margin-left: 0.5rem;
        }

        .error {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
        }

        .error-text {
          color: #dc3545;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          display: block;
        }

        .image-drop-zone {
          border: 2px dashed #ddd;
          border-radius: 8px;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.3s;
          background-color: #fafafa;
        }

        .image-drop-zone:hover:not(.disabled) {
          border-color: #007bff;
          background-color: #f8f9ff;
        }

        .image-drop-zone.disabled {
          cursor: not-allowed;
          opacity: 0.6;
          background-color: #f5f5f5;
        }

        .spinner {
          display:inline-block;width:1em;height:1em;margin-right:.5em;
          border:2px solid currentColor;border-right-color:transparent;border-radius:50%;
          animation: spin .6s linear infinite;vertical-align:-0.125em;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        
        .image-previews {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .image-preview-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .image-preview {
          width: 100px;
          height: 100px;
          object-fit: cover;
          border-radius: 8px;
          border: 2px solid #ddd;
        }

        .remove-image-btn {
          position: absolute;
          top: -8px;
          right: calc(50% - 58px);
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          cursor: pointer;
          font-size: 16px;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .remove-image-btn:hover {
          background: #c82333;
        }

        .image-info {
          font-size: 0.75rem;
          color: #6c757d;
          text-align: center;
          margin-top: 0.25rem;
          word-break: break-all;
        }

        .upload-info {
          color: #6c757d;
          font-size: 0.875rem;
          margin-top: 0.5rem;
          display: block;
        }

        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        select, textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }

        select:focus, textarea:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      `}</style>
    </div>
  );
};

export default ReportIncident;